import React from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {Delete as DeleteIcon, DragIndicator as DragIndicatorIcon} from '@mui/icons-material';
import {DragDropContext, Droppable, Draggable, DropResult} from 'react-beautiful-dnd';

import {Types} from '@wheeebo/polaris-api';
import {AreaMap, AreaType, useGoogleMap, RightDrawer, TooltipButton} from '@wheeebo/polaris-ui';

import {config} from '@components/Constants';

const INITIAL_ITEM_COUNT = 25;

interface RowProps {
  id: number;
  editable: boolean;
  onDelete: (rowIndex: number) => void;
}

function Row({id, editable, onDelete}: RowProps) {
  return (
    <>
      <TableCell width="20px">{editable ? <DragIndicatorIcon color="disabled" /> : null}</TableCell>
      <TableCell padding="checkbox">{id + 1}</TableCell>
      <TableCell align="right">
        {editable ? (
          <TooltipButton message="Delete" icon={<DeleteIcon fontSize="small" />} onClick={() => onDelete(id)} />
        ) : null}
      </TableCell>
    </>
  );
}

interface Props {
  areaContainer: Types.AreaContainer;
  onUpdate: (area: Types.Area) => void;
  areaType: AreaType;
  editable?: boolean;
  children?: React.ReactNode;
  title: string;
  subHeader: string;
}

function AreaEditForm({
  areaContainer,
  onUpdate,
  areaType,
  editable = false,
  children,
  title,
  subHeader,
}: Props): JSX.Element {
  const {center, setCenter} = useGoogleMap();
  const [page, setPage] = React.useState(0);
  const handleRowDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const srcIndex = result.source.index;
    const dstIndex = result.destination?.index;
    if (srcIndex === dstIndex) {
      return;
    }
    const points = areaContainer.area?.at(0);
    if (!points) {
      return;
    }
    const draggedElement = points.splice(srcIndex, 1)[0];
    // Insert the draggedElement to dstIndex.
    points.splice(dstIndex, 0, draggedElement);
    // If the values of first and last elements should be the same to make a closed area.
    const first = points.at(0);
    const last = points.at(points.length - 1);
    if (first && last) {
      last[0] = first[0];
      last[1] = first[1];
    }
    onUpdate([points]);
  };
  const handleAddRow = () => {
    if (!areaContainer) {
      return;
    } else if (!areaContainer.area) {
      areaContainer.area = [[]];
    }
    const points = areaContainer.area.at(0);
    if (!points) {
      return;
    }
    // Insert the lat and lng of the center in the map at the second postion from the end.
    points.splice(points.length - 1, 0, [center?.lng || 0, center?.lat || 0]);
    // If this is the first row, add more points to reate a rectangle.
    if (points.length === 1) {
      const base = points[0];
      points.push([base[0] + 0.0, base[1] + 0.01]);
      points.push([base[0] + 0.01, base[1] + 0.01]);
      points.push([base[0] + 0.01, base[1] + 0.0]);
      points.push([base[0] + 0.0, base[1] + 0.0]);
    }
    onUpdate([points]);
  };
  const handleDeleteRow = (rowIndex: number) => {
    const points = areaContainer.area?.at(0);
    // The points should have at least three visible points plus the last one, which is
    // intentially hidden and contains the same value as the first one.
    if (!points || points.length <= 4) {
      return;
    }
    points.splice(rowIndex, 1);
    // The first and last ones should contains the same value to make a closed area.
    // If the first one is removed, the last value should be changed to the new first one.
    if (rowIndex === 0) {
      const first = points.at(0);
      const last = points.at(points.length - 1);
      if (first && last) {
        last[0] = first[0];
        last[1] = first[1];
      }
    }
    onUpdate([points]);
  };
  const handleMarkerDragEnd = (rowIndex: number, coord: google.maps.LatLngLiteral) => {
    const points = areaContainer.area?.at(0);
    if (!points || points.length <= rowIndex) {
      return;
    }
    // The selected marker was moved to the new position `coord`.
    const selected = points.at(rowIndex);
    if (selected) {
      selected[0] = coord.lng;
      selected[1] = coord.lat;
    }
    // The last value should be the same as the first one, so the first one was moved,
    // apply the same coordinate to the last one.
    if (rowIndex === 0) {
      const last = points.at(points.length - 1);
      if (last) {
        last[0] = coord.lng;
        last[1] = coord.lat;
      }
    }
    onUpdate([points]);
  };
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <AreaMap
          apiKey={config.GOOGLE_MAP_API_KEY || ''}
          onMarkerDragEnd={handleMarkerDragEnd}
          areaContainer={areaContainer}
          areaType={areaType}
          editable={editable}
        />
        <RightDrawer>
          <Card>
            <CardHeader title={title} subheader={subHeader} />
            <CardContent>
              <Stack gap={3}>
                {children}
                {areaContainer?.area && areaContainer.area.length > 0 ? (
                  <TableContainer component={Paper} elevation={0}>
                    <Typography variant="caption">Corners</Typography>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width="20px" />
                          <TableCell align="left" padding="checkbox">
                            #
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableHead>
                      <DragDropContext onDragEnd={handleRowDragEnd}>
                        <Droppable droppableId="droppable">
                          {(provided) => (
                            <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                              {areaContainer.area
                                ?.at(0)
                                ?.slice(0, -1) // Hide the last one which should be the same as the first one.
                                ?.map((e: number[], index: number) => {
                                  const lat = e[1];
                                  const lng = e[0];
                                  const key = `${lat}_${lng}_${index}`;
                                  return (
                                    <Draggable key={key} draggableId={key} index={index} isDragDisabled={!editable}>
                                      {(provided) => (
                                        <TableRow
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          onClick={() => setCenter?.({lat, lng})}>
                                          <Row key={index} id={index} onDelete={handleDeleteRow} editable={editable} />
                                        </TableRow>
                                      )}
                                    </Draggable>
                                  );
                                })}
                              {provided.placeholder}
                            </TableBody>
                          )}
                        </Droppable>
                      </DragDropContext>
                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            sx={{
                              '.MuiTablePagination-displayedRows': {m: 1},
                            }}
                            backIconButtonProps={{sx: {p: 0}}}
                            nextIconButtonProps={{sx: {p: 0}}}
                            rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                            count={areaContainer.area?.at(0)?.length || 0}
                            rowsPerPage={INITIAL_ITEM_COUNT}
                            page={page}
                            SelectProps={{
                              inputProps: {'aria-label': 'rows per page'},
                            }}
                            onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) =>
                              setPage(newPage)
                            }
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2">No record found.</Typography>
                )}
                {editable ? (
                  <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly'}}>
                    <Button type="submit" color="info" onClick={handleAddRow}>
                      Add a row
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </RightDrawer>
      </Grid>
    </Grid>
  );
}

export default React.memo(AreaEditForm);
